// 試合盤面の表示プリミティブ (= Board.tsx から切り出した小部品群、 ADR-0004 300 LOC 制約対応)。
//
// CardTile / FieldRow / HiddenOrCards / OpponentHand / PlayerHand / StatLine

import type { CardView } from "@/bindings/CardView";
import type { ChargeView } from "@/bindings/ChargeView";
import type { HandView } from "@/bindings/HandView";
import type { PlayerView } from "@/bindings/PlayerView";
import type { SetZoneView } from "@/bindings/SetZoneView";
import { useCardCatalog } from "./cardCatalog";

const FIELD_SLOTS = 5;

// 1 枚分の表示。 name が null なら裏面、 公開なら name + atk/def。
// selected = true で cyan/emerald ハイライト、 clickable で pointer cursor。
// tapped = true で CSS rotate-90 (= 横倒し)。
export function CardTile(props: {
  card?: CardView | null | undefined;
  faceDown?: boolean | undefined;
  tapped?: boolean | undefined;
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  selected?: boolean | undefined;
  clickable?: boolean | undefined;
}): JSX.Element {
  const { card, faceDown, tapped, onClick, disabled, selected, clickable } = props;
  const { setHoveredId } = useCardCatalog();
  const isHidden = faceDown === true || card == null || card.name === null;
  const rotateClass = tapped === true ? "rotate-90" : "";

  const base =
    "flex h-16 w-12 flex-col items-center justify-center rounded border text-center text-[9px] leading-tight";

  if (isHidden) {
    return (
      <div className={`${base} ${rotateClass} border-slate-600 bg-slate-700 text-slate-400`}>
        ▭
      </div>
    );
  }

  // hover で catalog 詳細を CardDetail に通知 (= 公開カードのみ)。
  const hoverProps = {
    onMouseEnter: () => setHoveredId(card.card_id),
    onMouseLeave: () => setHoveredId(null),
  };

  const content = (
    <>
      <span className="line-clamp-2 px-0.5 font-medium">{card.name}</span>
      {card.atk !== null && card.def !== null && (
        <span className="text-slate-400">
          {card.atk}/{card.def}
        </span>
      )}
    </>
  );

  if (onClick !== undefined || clickable === true) {
    const borderColor = selected === true ? "border-cyan-400" : "border-emerald-500";
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        {...hoverProps}
        className={`${base} ${rotateClass} ${borderColor} bg-slate-800 text-emerald-100 hover:bg-slate-700 disabled:opacity-50`}
      >
        {content}
      </button>
    );
  }

  const borderColor = selected === true ? "border-cyan-400" : "border-slate-500";
  return (
    <div
      {...hoverProps}
      className={`${base} ${rotateClass} ${borderColor} bg-slate-800 text-slate-100`}
    >
      {content}
    </div>
  );
}

// 空き枠込みの field 5 枠。 optional な onCardClick / selectedId / clickable を受ける。
export function FieldRow(props: {
  field: CardView[];
  onCardClick?: ((card: CardView) => void) | undefined;
  selectedId?: string | null | undefined;
  clickable?: boolean | undefined;
  disabled?: boolean | undefined;
}): JSX.Element {
  const { field, onCardClick, selectedId, clickable, disabled } = props;
  const slots = Array.from({ length: FIELD_SLOTS }, (_, i) => field[i]);
  return (
    <div className="flex gap-1">
      {slots.map((card, i) => {
        if (card === undefined) {
          return (
            <div
              key={`empty-${String(i)}`}
              className="flex h-16 w-12 items-center justify-center rounded border border-dashed border-slate-700 text-slate-600"
            >
              _
            </div>
          );
        }
        return (
          <CardTile
            key={card.instance_id}
            card={card}
            tapped={card.tapped}
            onClick={
              onCardClick !== undefined && clickable === true
                ? () => onCardClick(card)
                : undefined
            }
            selected={selectedId === card.instance_id}
            clickable={clickable}
            disabled={disabled}
          />
        );
      })}
    </div>
  );
}

// charge / set zone: cards が null なら count 枚の裏面、 配列なら内容表示。
export function HiddenOrCards(props: {
  label: string;
  view: ChargeView | SetZoneView;
}): JSX.Element {
  const { label, view } = props;
  const cards =
    view.cards ??
    Array.from({ length: view.count }, () => null as CardView | null);
  return (
    <div className="space-y-1">
      <span className="text-xs text-slate-400">
        {label} ({view.count})
      </span>
      <div className="flex gap-1">
        {cards.length === 0 ? (
          <span className="text-xs text-slate-600">-</span>
        ) : (
          cards.map((card, i) => (
            <CardTile
              key={card?.instance_id ?? `hidden-${String(i)}`}
              card={card}
              faceDown={card === null}
            />
          ))
        )}
      </div>
    </div>
  );
}

// 相手 (上段) の hand: Hidden は枚数分の裏面、 Visible でも内容を晒さず裏面で揃える。
export function OpponentHand(props: { hand: HandView }): JSX.Element {
  const count =
    "Hidden" in props.hand
      ? props.hand.Hidden.count
      : props.hand.Visible.cards.length;
  return (
    <div className="space-y-1">
      <span className="text-xs text-slate-400">Hand ({count} 裏)</span>
      <div className="flex gap-1">
        {Array.from({ length: count }, (_, i) => (
          <CardTile key={`opp-hand-${String(i)}`} faceDown />
        ))}
      </div>
    </div>
  );
}

// 自分 (下段) の hand: Visible なら各カードを公開、 Main phase で click 召喚。
// canSetInitialCharge / canCharge が true の場合は click で 初期 charge / Charge 配置。
export function PlayerHand(props: {
  hand: HandView;
  canSummon: boolean;
  canSetInitialCharge: boolean;
  canCharge: boolean;
  busy: boolean;
  onSummon: (card: CardView) => void;
  onSetInitialCharge: (card: CardView) => void;
  onCharge: (card: CardView) => void;
}): JSX.Element {
  const {
    hand,
    canSummon,
    canSetInitialCharge,
    canCharge,
    busy,
    onSummon,
    onSetInitialCharge,
    onCharge,
  } = props;
  if ("Hidden" in hand) {
    return (
      <div className="space-y-1">
        <span className="text-xs text-slate-400">Hand ({hand.Hidden.count})</span>
        <div className="flex gap-1">
          {Array.from({ length: hand.Hidden.count }, (_, i) => (
            <CardTile key={`my-hand-${String(i)}`} faceDown />
          ))}
        </div>
      </div>
    );
  }
  const clickable = canSetInitialCharge || canCharge || canSummon;
  const label = canSetInitialCharge
    ? " ← click で 初期 charge"
    : canCharge
      ? " ← click で CHARGE"
      : canSummon
        ? " ← click で SUMMON"
        : "";
  const handleClick = (card: CardView): (() => void) | undefined => {
    if (canSetInitialCharge) return () => onSetInitialCharge(card);
    if (canCharge) return () => onCharge(card);
    if (canSummon) return () => onSummon(card);
    return undefined;
  };
  return (
    <div className="space-y-1">
      <span className="text-xs text-slate-400">
        Hand ({hand.Visible.cards.length})
        {label}
      </span>
      <div className="flex gap-1">
        {hand.Visible.cards.map((card) => (
          <CardTile
            key={card.instance_id}
            card={card}
            onClick={handleClick(card)}
            disabled={busy || !clickable}
          />
        ))}
      </div>
    </div>
  );
}

// charge zone の可視化。 self (= cards 配列あり) は CardTile 列で render。
// face_up なら name 表示、 face_down なら裏面。 tapped なら横倒し。
// 相手 (cards = null) は count のみ表示。
export function ChargeRow(props: {
  charge: ChargeView;
}): JSX.Element {
  const { charge } = props;
  // wasm-bindgen は Option::None を `null` ではなく key 省略で送る (= JS で undefined)。
  // 型は `Array<CardView> | null` だが実体は `... | undefined` も来るので == で両対応する。
  const cards = charge.cards;
  if (cards == null) {
    return (
      <div className="flex h-16 w-12 flex-col items-center justify-center rounded border border-slate-700 text-[9px] text-slate-400">
        <span>CHARGE</span>
        <span>{charge.count}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] text-slate-400">CHARGE ({charge.count})</span>
      <div className="flex gap-0.5">
        {cards.length === 0 ? (
          <span className="text-xs text-slate-600">-</span>
        ) : (
          cards.map((card, i) => (
            <CardTile
              key={card.instance_id ?? `charge-${String(i)}`}
              card={card.face_up ? card : null}
              faceDown={!card.face_up}
              tapped={card.tapped}
            />
          ))
        )}
      </div>
    </div>
  );
}

// LP / Deck / Grave の数値ヘッダ。
export function StatLine(props: {
  label: string;
  player: PlayerView;
}): JSX.Element {
  const { label, player } = props;
  return (
    <div className="flex items-center gap-4 text-sm font-medium">
      <span className="text-base font-semibold">{label}</span>
      <span>LP{player.life}</span>
      <span>Deck{player.deck_count}</span>
      <span>Grave{player.grave.length}</span>
    </div>
  );
}
